import { consumeNextSendMethod } from "@/analytics/composerIntent";
import type { MessageInputMethod, MessageSurface } from "@/analytics/surfaces";
import { useCreateFlowSession, useFlowSessionById } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { usePrivateWorkspace } from "@/api/workspaces";
import { validateChatMessageFiles } from "@/constants/file-limits";
import { useSessionContext } from "@/context/SessionContext";
import { HarnessDraft } from "@/hooks/useHarnessDraft";
import {
  trackComposerAttachmentAdded,
  trackMessageSent,
} from "@/utils/analytics";
import { wait } from "@/utils/wait";
import {
  AttachedFile,
  FileRecord,
  FLOW_SESSION_MODES,
  FlowSession,
  PostMessageParams,
} from "@cohesive/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { UploadTarget, useFileRecordUpload } from "./useFileRecordUpload";

interface FlowSessionCreatedParams {
  flowSession: FlowSession;
}

interface SendMeta {
  inputMethod: MessageInputMethod;
}

interface AttachMeta {
  method: "picker" | "drop_os" | "drop_in_app" | "paste";
}

interface MessageSentParams extends PostMessageParams {
  flowSession: FlowSession;
}

interface UseMessageActionsProps {
  /** Where this composer lives — stamped on every Message Sent. */
  surface: MessageSurface;
  /**
   * Awaited before the flow proceeds — a callback that writes session settings
   * (e.g. the composer's draft confirmation mode) can return a promise and have
   * its write ordered ahead of the first message.
   */
  onFlowSessionCreated?: (
    params: FlowSessionCreatedParams,
  ) => void | Promise<void>;
  onMessageSent?: (params: MessageSentParams) => void;
  /**
   * Workspace the auto-created flow session is scoped to. Defaults to the
   * user's private workspace, which is what every unscoped surface wants.
   */
  workspaceId?: string;
  /**
   * Harness + model to stamp on the flow session this hook mints. The stamp stays re-stampable
   * via the update endpoint until the first message spends it, but a composer that offers the
   * choice still hands it down here so the session is born on it. Omitted starts the chat on
   * Cohesive, as every chat did before.
   */
  harnessSelection?: HarnessDraft;
  /**
   * Where uploads land for a composer whose session does not exist until
   * its own send (the new home starts a canvas): no session is minted on
   * attach, and the caller reads `readyFileRecordIds` for that send.
   */
  attachTarget?: UploadTarget;
}

export const useMessageActions = ({
  surface,
  onFlowSessionCreated,
  onMessageSent,
  workspaceId,
  harnessSelection,
  attachTarget,
}: UseMessageActionsProps) => {
  const { sessionId, setCurrentSessionId } = useSessionContext();
  const { data: flowSession } = useFlowSessionById(sessionId || "");

  // True while the conversation the user is composing had no session yet —
  // attaching a file mints one via ensureFlowSession before the first send,
  // so send-time sessionId cannot answer "is this a new conversation".
  // Re-arms whenever the composer goes session-less again (e.g. the home
  // composer resets after handing the chat off).
  const composeStartedWithoutSessionRef = useRef(!sessionId);
  useEffect(() => {
    if (!sessionId) {
      composeStartedWithoutSessionRef.current = true;
    }
  }, [sessionId]);

  const [isLoading, setIsLoading] = useState(false);

  const createFlowSession = useCreateFlowSession();
  const postFlowMessage = usePostFlowMessage();
  const privateWorkspace = usePrivateWorkspace();
  const targetWorkspaceId = workspaceId ?? privateWorkspace?.id;

  const {
    uploadedFiles,
    readyFileRecordIds,
    enqueueFiles,
    enqueueExistingRecords,
    removeFile,
    clearFiles,
  } = useFileRecordUpload();

  // Chat auto-creates a flow session on first attach so uploads (and
  // dropped file-record copies) have a real `flow_session_id` to scope
  // against. Auto-created sessions here are always standalone chat (no
  // canvas) — canvas chats are created upstream by `CanvasSessionPane`
  // with the canvas_id baked in.
  const ensureFlowSession = useCallback(async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    const newFlowSession = await createFlowSession.mutateAsync({
      mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
      // Scope to the caller's workspace (private workspace by default) so
      // it satisfies the workspace_id requirement.
      class: "workspace",
      workspace_id: targetWorkspaceId,
      harness: harnessSelection?.harness,
      model: harnessSelection?.model,
    });

    setCurrentSessionId(newFlowSession.id);

    await onFlowSessionCreated?.({
      flowSession: newFlowSession,
    });

    if (!newFlowSession.id) {
      console.error("Flow session ID is undefined");
      return null;
    }
    return newFlowSession.id;
  }, [
    sessionId,
    createFlowSession,
    setCurrentSessionId,
    onFlowSessionCreated,
    targetWorkspaceId,
    harnessSelection,
  ]);

  const onAttachFile = useCallback(
    async (files: File[] | null, meta?: AttachMeta) => {
      if (!files) return;

      const newFiles = files.map((file) => ({ file, name: file.name }));
      const currentFiles = uploadedFiles.map((uf) => ({
        file: uf.file,
        name: uf.name,
      }));
      const allFiles = [...currentFiles, ...newFiles];

      const validation = validateChatMessageFiles(allFiles);
      if (!validation.isValid) {
        toast.error(validation.error || "File size limit exceeded");
        return;
      }

      let target = attachTarget;
      if (!target) {
        const flowSessionId = await ensureFlowSession();
        if (!flowSessionId) return;

        // Chat attachments are always flow-session-scoped — they belong
        // to the message, not to the surrounding canvas. Files attached
        // here don't show up as canvas nodes; the canvas agent still
        // sees them because `file-list` walks both canvas-class and the
        // active session-class. Storage is cleaned up with the session.
        // The upload itself is fire-and-forget — SSE settles status back
        // into the queue once the S3 webhook lands.
        target = { class: "flow-session", flow_session_id: flowSessionId };
      }

      await enqueueFiles(files, target);

      trackComposerAttachmentAdded({
        method: meta?.method ?? "picker",
        count: files.length,
        surface,
      });
    },
    [attachTarget, ensureFlowSession, enqueueFiles, uploadedFiles, surface],
  );

  const onRemoveFile = useCallback(
    (file: AttachedFile) => {
      removeFile(file);
    },
    [removeFile],
  );

  const onSendMessage = useCallback(
    async (params: PostMessageParams, meta?: SendMeta) => {
      const isNewSession = composeStartedWithoutSessionRef.current;
      // Consume the marker before any await: its TTL must measure the gap
      // between the chip click and the send, not the network round-trip.
      // A follow-up/suggested-action chip fills the composer and the user
      // hits Enter — the marked method wins over MessageInput's "typed";
      // explicit non-typed methods (voice, retry) always win.
      const markedMethod = consumeNextSendMethod();
      const inputMethod =
        meta?.inputMethod && meta.inputMethod !== "typed"
          ? meta.inputMethod
          : (markedMethod ?? meta?.inputMethod ?? "typed");
      try {
        setIsLoading(true);
        let effectiveFlowSessionId: string;
        // Cache-miss cast below can leave this undefined — read it optionally.
        let resolvedFlowSession: FlowSession | undefined;

        if (!sessionId) {
          const newFlowSession = await createFlowSession.mutateAsync({
            mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
            class: "workspace",
            workspace_id: targetWorkspaceId,
            harness: harnessSelection?.harness,
            model: harnessSelection?.model,
          });

          // Awaited so a callback that writes session settings lands before
          // the message below — the 150ms cushion isn't an ordering guarantee.
          await onFlowSessionCreated?.({
            flowSession: newFlowSession,
          });

          setCurrentSessionId(newFlowSession.id);

          if (!newFlowSession.id) {
            throw new Error("Flow session ID is undefined");
          }

          await wait(150);

          effectiveFlowSessionId = newFlowSession.id;
          resolvedFlowSession = newFlowSession;
        } else {
          effectiveFlowSessionId = sessionId;
          resolvedFlowSession = flowSession;
        }

        // `readyFileRecordIds` is pulled straight off the queue — ids once
        // the S3 POST finished (server may still be `processing`). Never
        // ships a tempId or a mid-upload placeholder on the wire.
        const mergedParams: PostMessageParams = {
          ...params,
          file_record_ids: [
            ...(params.file_record_ids ?? []),
            ...readyFileRecordIds,
          ],
        };

        await postFlowMessage.mutateAsync({
          flowSessionId: effectiveFlowSessionId,
          ...mergedParams,
        });

        const referencedEntities = params.referenced_entities ?? [];
        composeStartedWithoutSessionRef.current = false;
        trackMessageSent({
          surface,
          inputMethod,
          charCount: params.content?.length ?? 0,
          attachmentCount: mergedParams.file_record_ids?.length ?? 0,
          mentionCount: referencedEntities.length,
          mentionTypes: [
            ...new Set(referencedEntities.map((e) => e.entity_type)),
          ],
          skillCount: referencedEntities.filter(
            (e) => e.entity_type === "skill",
          ).length,
          reasoningEffort: params.reasoning_effort,
          model: params.model,
          harness: resolvedFlowSession?.harness,
          sessionClass: resolvedFlowSession?.class,
          sessionMode: resolvedFlowSession?.mode,
          canvasId: resolvedFlowSession?.canvas_id,
          workspaceId: resolvedFlowSession?.workspace_id,
          // The per-message permission mode wins (it rides the send and
          // stamps the session); the session's stored mode is the fallback.
          confirmationMode:
            params.permission_mode ?? resolvedFlowSession?.permission_mode,
          isNewSession,
          flowSessionId: effectiveFlowSessionId,
        });

        onMessageSent?.({
          ...mergedParams,
          flowSession: resolvedFlowSession as FlowSession,
        });

        clearFiles();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [
      createFlowSession,
      sessionId,
      postFlowMessage,
      readyFileRecordIds,
      clearFiles,
      setCurrentSessionId,
      flowSession,
      onFlowSessionCreated,
      onMessageSent,
      targetWorkspaceId,
      harnessSelection,
      surface,
    ],
  );

  const onAttachFileRecords = useCallback(
    (records: FileRecord[], meta?: AttachMeta) => {
      if (records.length === 0) return;

      // Same gate `onAttachFile` applies before enqueuing: count the incoming
      // references against what's already queued so the per-message cap
      // surfaces as a toast at attach time, not as a silently-disabled send
      // button. References have no client-side bytes, so entries without a
      // `file` payload skip the size check inside the validator.
      const validation = validateChatMessageFiles([
        ...uploadedFiles.map((uf) => ({ name: uf.name })),
        ...records.map((record) => ({
          name: record.path.split("/").filter(Boolean).pop() ?? record.path,
        })),
      ]);
      if (!validation.isValid) {
        toast.error(validation.error || "Too many files attached");
        return;
      }

      enqueueExistingRecords(records);

      trackComposerAttachmentAdded({
        method: meta?.method ?? "picker",
        count: records.length,
        surface,
      });
    },
    [enqueueExistingRecords, uploadedFiles, surface],
  );

  return {
    uploadedFiles,
    readyFileRecordIds,
    clearFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    onSendMessage,
    ensureFlowSession,
    isLoading:
      createFlowSession.isPending || postFlowMessage.isPending || isLoading,
  };
};
